import { ConnectionStatus } from '#pplx/frontend/apps/ask/connectors/consts/connectionStatus.js';
import { formatConnectorName } from '#pplx/frontend/apps/ask/files/file-utils/fileUtils.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
export const PROVIDER_FOOTNOTES = {
    merge: {
        link: 'https://trust.perplexity.ai/subprocessors',
        text: 'This connector is available through Merge API, Inc. By adding this connector, you agree that Merge will be a data sub-processor.',
    },
    pipedream: {
        link: 'https://trust.perplexity.ai/subprocessors',
        text: 'This connector is available through Pipedream, Inc. By adding this connector, you agree that Pipedream will be a data sub-processor.',
    },
};
/**
 * Checks whether source_details contains any meaningful metadata worth
 * showing in the enable-connector modal (bullets, links, footnote, etc.).
 */
export function hasSourceDetailsMetadata(sourceDetails, provider) {
    if (provider && provider in PROVIDER_FOOTNOTES) {
        return true;
    }
    if (!sourceDetails) {
        return false;
    }
    const { bullets, connected_bullets, consumer_bullets, developer, website_url, documentation_url, support_url, footnote, } = sourceDetails;
    const hasBullets = Boolean(bullets?.length || connected_bullets?.length || consumer_bullets?.length);
    const hasLinks = Boolean(website_url || documentation_url || support_url);
    // Deliberately does not count sample_queries. This decides whether the DDB
    // record replaces the static CONNECTOR_DETAILS baseline wholesale, so counting
    // them would let a source seeded with only queries discard its static bullets,
    // links, and developer. Callers merge sample queries onto whichever base wins.
    return Boolean(hasBullets || hasLinks || footnote || developer);
}
export const toSampleQueries = (sampleQueries) => 
// Destructuring a string would silently yield `undefined` for both fields
// rather than failing, so the legacy shape has to be recognized explicitly.
(sampleQueries ?? []).map((entry) => typeof entry === 'string'
    ? { query: entry, displayForm: null }
    : { query: entry.query, displayForm: entry.display_form ?? null });
/**
 * Resolve an ID-only modal request against the visible sources catalog.
 *
 * During connector migrations, the execution target can be hidden while its
 * legacy catalog row remains visible. That row is useful for presentation, but
 * its ID must never replace the target ID used for setup and authentication.
 */
export function resolveConnectorModalSources(connectorName, catalogSources) {
    const source = catalogSources.find((candidate) => candidate.id === connectorName);
    if (source) {
        return { source };
    }
    const presentationSource = catalogSources.find((candidate) => {
        if (!('migration' in candidate) || !candidate.migration) {
            return false;
        }
        return (candidate.migration.target_source_id === connectorName &&
            candidate.migration.legacy_source_ids.includes(candidate.id));
    });
    return presentationSource ? { presentationSource } : {};
}
export function buildConnectorDetailsFromSource({ sourceDetails, connectorName, connectionStatus, isEnterprise, displayName, description, provider, }) {
    const links = [];
    if (sourceDetails.website_url) {
        links.push({ label: 'Website', url: sourceDetails.website_url, icon: $icon('link') });
    }
    if (sourceDetails.documentation_url) {
        links.push({
            label: 'Documentation',
            url: sourceDetails.documentation_url,
            icon: $icon('book'),
        });
    }
    if (sourceDetails.support_url) {
        links.push({ label: 'Support', url: sourceDetails.support_url, icon: $icon('help') });
    }
    let finalBullets = sourceDetails.bullets ?? [];
    if (connectionStatus === ConnectionStatus.CONNECTED && sourceDetails.connected_bullets?.length) {
        finalBullets = sourceDetails.connected_bullets;
    }
    else if (!isEnterprise && sourceDetails.consumer_bullets?.length) {
        finalBullets = sourceDetails.consumer_bullets;
    }
    const footnote = sourceDetails.footnote ?? (provider ? (PROVIDER_FOOTNOTES[provider] ?? null) : null);
    return {
        name: displayName || formatConnectorName(connectorName),
        tagline: description ?? null,
        footnote,
        bullets: [...finalBullets],
        links,
        developer: sourceDetails.developer ?? null,
        sampleQueries: toSampleQueries(sourceDetails.sample_queries),
    };
}
//# sourceMappingURL=connectorDetails.js.map